import React from "react";
import { Modal, OverlayTrigger } from "react-bootstrap";
import styled from "styled-components";
import { ReportReadonlyMessage } from "../../components/messages";
import { Button, DateDisplay, ProfileDisplay } from "../../components";
import { ThemedSpan } from "../../components/utilities";
import { MessagePriorities, ReminderStatuses } from "../../helpers/constants";
import classNames from "classnames";
import useOpenDocument from "../../hooks/useOpenDocument";
import { ClickableIcon } from "../../components/buttons";

const ModalBody = styled(Modal.Body)`
    min-height: 66.67vh;
    max-height: 66.67vh;
    display: flex;
    flex-direction: column;
    padding: 0;
`;

const ReminderPriorityPill = ({ value, isInfoOnly }) => {

    if (!value || isInfoOnly) return <span className="py-1 px-2">-</span>

    const className = [{
        'bg-priority-high text-priority-high': value === MessagePriorities.High,
        'bg-priority-medium text-priority-medium': value === MessagePriorities.Medium,
        'bg-priority-low text-priority-low': value === MessagePriorities.Low
    }];

    return <span className={classNames("badge py-1 px-2", className)}>
        {{
            [MessagePriorities.Low]: "Low",
            [MessagePriorities.Medium]: "Medium",
            [MessagePriorities.High]: "High"
        }[value]}
    </span>;
};

const OpenDocumentButton = ({
    documentId,
    label = "Open",
    icon = "file",
    ...rest }) => {
    const { openDocument, isFetching: isOpeningDocument } = useOpenDocument();

    const handleDocumentOpen = () => {
        if (isOpeningDocument)
            return;

        openDocument(documentId);
    }

    return <ClickableIcon
            onClick={handleDocumentOpen}
            icon={icon}
            disabled={isOpeningDocument}
            {...rest}
        />
}

const ViewEventModal = ({show, reminder, handleClose = () => {}}) => {

    const [expanded, setExpanded] = React.useState(false);
    const [showLoadMore, setShowLoadMore] = React.useState(false);
    const contentRef = React.useRef(null);

    React.useEffect(() => {
        // Check if content is overflowing
        const el = contentRef.current;
        if (el && el.scrollHeight > el.clientHeight) {
        setShowLoadMore(true);
        }
    }, []);

    const {
        id,
        description,
        notes,
        deadlineDate,
        priority,
        appointmentId,
        projectId,
        project,
        userId,
        type,
        isTypeVisible,
        masterAccountId,
        masterAccountName,
        attachments,
        isLocked
    } = reminder || {};

    return (
         <Modal size="xl" centered backdrop="static" show={show} onHide={handleClose} className="overflow-scroll">
            <Modal.Header closeButton>
                <Modal.Title>{description}</Modal.Title>
            </Modal.Header>
            <ModalBody className="overflow-scroll">
               <div>
                    {isLocked && <ReportReadonlyMessage text="This event is now readonly." />}
               </div>
                <div className="p-3">
                    <div className="p-1 mb-2">
                        <div className="card">
                            <div className="card-body">
                                <p>
                                    <ThemedSpan variant="primary" className="fs-4">
                                        Event Details
                                    </ThemedSpan>
                                </p>
                                <div className="row mb-2">
                                    <div className="col-4">
                                        <span className="fw-bold me-1">Type:</span> 
                                        <span className="me-1">{type}</span> 
                                    </div>
                                    <div className="col-4">
                                        <span className="fw-bold me-1">Project:</span> 
                                        <span className="me-1">{project}</span> 
                                    </div>
                                     <div className="col-4">
                                        <span className="fw-bold me-1">Priority:</span> 
                                        <ReminderPriorityPill value={priority} />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 d-flex flex-row">
                                        <span className="fw-bold me-1">Created By:</span> 
                                        <ProfileDisplay userId={userId} />
                                    </div>
                                    <div className="col-6">
                                        <span className="fw-bold me-1">Date:</span> 
                                        <span className="me-1"><DateDisplay format="DD/MM/YYYY HH:mm" className="font-monospace">{deadlineDate}</DateDisplay></span>
                                    </div>
                                </div>
                                {notes != null && notes !== "" && <div className="row mb-2">
                                    <div className="relative">
                                        <div ref={contentRef}
                                            className={`overflow-hidden transition-all duration-300 ${
                                            expanded ? "max-h-none" : "max-h-40"
                                        }`}>
                                            <div className="col-12">
                                                <span className="fw-bold me-1">Notes</span> 
                                                
                                            </div>
                                            <div className="col-12">
                                                <span className="me-1">{notes}</span> 
                                            </div>
                                        </div>
                                    </div>
                                    {!expanded && showLoadMore && (
                                        <div className="text-blue-500 cursor-pointer mt-2" onClick={() => setExpanded(true)}>
                                        Load More
                                        </div>
                                    )}
                                </div>}
                            </div>
                        </div>
                    </div>
                    {attachments != null && attachments?.length > 0 && 
                     <div className="d-flex flex-row">
                        <div className="col-6 p-1">
                            <div className="card">
                                <div className="card-body">
                                    <div className="fs-4 mb-2">
                                        <ThemedSpan variant="primary">Attachments</ThemedSpan>
                                    </div>
                                    <div>
                                       {attachments.map((att, i) => (
                                         <div className="d-flex flex-row justify-content-between align-items-center" key={i}>
                                            <div><OpenDocumentButton
                                                className="me-2"
                                                documentId={att?.documentId}
                                            />{att?.fileName}</div>
                                            <div>
                                                <ProfileDisplay userId={userId} />
                                            </div>
                                        </div>
                                       ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>}
                </div>
            </ModalBody>
            <Modal.Footer>
                <Button variant="light" onClick={handleClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ViewEventModal;